import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function AccountScreen() {
  const navigate = useNavigate();
  const { user, logout, loading } = useAuth();
  const [orderCount, setOrderCount] = useState(0);
  const [activeOrder, setActiveOrder] = useState(null);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.id) {
      fetch(`https://eleven11-i6v4.onrender.com/api/orders?userId=${user.id}&email=${encodeURIComponent(user.email)}`)
        .then(r => r.json())
        .then(data => {
          const orders = data.orders || [];
          setOrderCount(orders.length);

          const active = orders.find(o =>
            o.fulfillmentStatus !== "delivered" && o.items?.length > 0
          );
          setActiveOrder(active || null);
        })
        .catch(() => {});
    }
  }, [user]);

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingCard}>Loading account...</div>
      </div>
    );
  }

  if (!user) return null;

  const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();



const STAGE_LABELS = {
  order_placed:      "Order Placed",
  payment_confirmed: "Payment Confirmed",
  packaged:          "Packaged",
  shipped:           "Shipped",
  out_for_delivery:  "Out for Delivery",
  delivered:         "Delivered"
};

const trackingValue = activeOrder
  ? STAGE_LABELS[activeOrder.fulfillmentStatus] || "In Progress"
  : "—";

const trackingSubtext = activeOrder
  ? activeOrder.orderNumber
  : "No active shipments";

const trackingClick = activeOrder
  ? () => navigate(`/account/orders/${activeOrder._id}`)
  : undefined;

  const memberSince = user.createdAt
    ? new Date(user.createdAt).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      })
    : "Active member";

  return (
    <div style={styles.page}>
      <div style={styles.heroGlow} />

      <main style={styles.container}>
        <section style={styles.header}>
          <div>
            <p style={styles.kicker}>ELEVEN ACCOUNT</p>
            <h1 style={styles.title}>Profile</h1>
          </div>



       <button
  style={styles.logoutButton}
  onClick={async () => {

    localStorage.removeItem("user");

    await logout();

    navigate("/");

  }}
>
  Logout
</button>


        </section>

        <section style={styles.profileCard}>
          <div style={styles.avatarWrap}>
            <div style={styles.avatar}>
              {user.firstName?.[0] || "E"}
            </div>
            <div style={styles.verifiedDot}>✓</div>
          </div>

          <div style={styles.profileInfo}>
            <h2 style={styles.name}>{fullName || "ELEVEN User"}</h2>
            <p style={styles.email}>{user.email}</p>

            <div style={styles.badgeRow}>
              <span style={styles.badge}>
                {user.role === "admin" ? "Admin" : "Customer"}
              </span>
              <span style={styles.memberText}>{memberSince}</span>
            </div>
          </div>

          <button style={styles.editButton}>Edit</button>
        </section>

        <section style={styles.grid}>
   <InfoCard label="Orders" value={String(orderCount)} subtext="Completed purchases" onClick={() => navigate("/account/orders")} />
       <InfoCard
  label="Tracking"
  value={trackingValue}
  subtext={trackingSubtext}
  onClick={trackingClick}
/>


    <InfoCard label="Store Credit" value={`$${Number(user.storeCredit || 0).toFixed(2)}`} subtext="Available balance" />
<InfoCard label="Account ID" value={user.id?.slice(-6) || "—"} subtext="Secure profile" />
        </section>

        <section style={styles.card}>
          <SectionTitle title="Order Center" />

        <ActionRow
  title="Orders & Shipment Tracking"
  subtitle="View purchases, invoices, and live fulfillment updates"
  onClick={() => navigate("/account/orders")}
/>

          <ActionRow
            title="Returns & Support"
            subtitle="Start a return or contact ELEVEN support"
            onClick={() => navigate("/account/support")}
          />
        </section>

      

        <section style={styles.card}>
          <SectionTitle title="Personal Details" />

 <DetailRow label="Full Name" value={fullName || "—"} />
<DetailRow label="Email" value={user.email || "—"} />
<DetailRow label="Phone" value={user.phone ? `(${user.phone.replace(/\D/g,"").slice(0,3)}) ${user.phone.replace(/\D/g,"").slice(3,6)}-${user.phone.replace(/\D/g,"").slice(6)}` : "—"} />
<DetailRow label="Role" value={user.role || "customer"} />
        </section>

        <section style={styles.card}>
          <SectionTitle title="Account Settings" />

          <ActionRow
            title="Shipping Addresses"
            subtitle="Manage delivery locations"
            onClick={() => navigate("/account/addresses")}
          />

          <ActionRow
            title="Payment Methods"
            subtitle="Manage checkout payment options"
            onClick={() => navigate("/account/payments")}
          />

          <ActionRow
            title="Security"
            subtitle="Password, sessions, and account protection"
            onClick={() => navigate("/account/security")}
          />
        </section>

        {user.role === "admin" && (
          <section style={styles.adminCard}>
            <div>
              <p style={styles.adminLabel}>ADMIN ACCESS</p>
              <h3 style={styles.adminTitle}>ELEVEN Control Center</h3>
              <p style={styles.adminText}>
                Manage listings, inventory, orders, and marketplace operations.
              </p>
            </div>

            <button style={styles.adminButton} onClick={() => navigate("/admin")}>
              Open Admin
            </button>
          </section>
        )}

        <p style={styles.footerText}>
          Your ELEVEN profile details are private and securely stored.
        </p>
      </main>
    </div>
  );
}

function SectionTitle({ title }) {
  return <h3 style={styles.sectionTitle}>{title}</h3>;
}
function InfoCard({ label, value, subtext, onClick }) {
  const isLong = value && value.length > 6;
  return (
    <div style={{ ...styles.infoCard, cursor: onClick ? "pointer" : "default" }} onClick={onClick}>
      <p style={styles.infoLabel}>{label}</p>
      <h3 style={{
        ...styles.infoValue,
        fontSize: isLong ? 18 : 34,
        letterSpacing: isLong ? -0.3 : -1,
        marginTop: isLong ? 16 : 12
      }}>{value}</h3>
      <p style={styles.infoSubtext}>{subtext}</p>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={styles.detailRow}>
      <span style={styles.detailLabel}>{label}</span>
      <span style={styles.detailValue}>{value}</span>
    </div>
  );
}

function ActionRow({ title, subtitle, onClick }) {
  return (
    <button style={styles.actionRow} onClick={onClick}>
      <div>
        <p style={styles.actionTitle}>{title}</p>
        <p style={styles.actionSubtitle}>{subtitle}</p>
      </div>
      <span style={styles.chevron}>›</span>
    </button>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top, rgba(255,255,255,0.55), transparent 28%), linear-gradient(180deg, #f5f7fb 0%, #eef1f7 45%, #f7f8fb 100%)",
    color: "#050505",
    fontFamily: "-apple-system, BlinkMacSystemFont, Inter, Helvetica, Arial, sans-serif",
    position: "relative",
    overflow: "hidden",
    paddingTop: 90,
    paddingBottom: 80,
  },

  heroGlow: {
    position: "absolute",
    top: -180,
    left: "50%",
    transform: "translateX(-50%)",
    width: 720,
    height: 420,
    background:
      "radial-gradient(circle, rgba(255,255,255,0.9), rgba(210,220,240,0.35), transparent 70%)",
    filter: "blur(10px)",
    pointerEvents: "none",
  },

  container: {
    width: "min(1080px, calc(100% - 40px))",
    margin: "0 auto",
    position: "relative",
    zIndex: 2,
  },

  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 28,
  },

  kicker: {
    margin: 0,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 2.4,
    color: "#7b7f89",
  },

  title: {
    margin: "6px 0 0",
    fontSize: 48,
    letterSpacing: -1.5,
    fontWeight: 800,
  },

  logoutButton: {
    border: "1px solid rgba(0,0,0,0.08)",
    background: "rgba(255,255,255,0.75)",
    backdropFilter: "blur(18px)",
    borderRadius: 999,
    padding: "12px 20px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 14px 30px rgba(20,30,50,0.08)",
  },

  profileCard: {
    minHeight: 160,
    borderRadius: 34,
    background: "rgba(255,255,255,0.78)",
    backdropFilter: "blur(24px)",
    border: "1px solid rgba(255,255,255,0.8)",
    boxShadow: "0 28px 70px rgba(24,35,60,0.12)",
    display: "flex",
    alignItems: "center",
    gap: 26,
    padding: 34,
    marginBottom: 24,
  },

  avatarWrap: {
    position: "relative",
  },

  avatar: {
    width: 104,
    height: 104,
    borderRadius: "50%",
    background:
      "linear-gradient(145deg, #f6f7fa, #e6e9ef)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 44,
    fontWeight: 800,
    color: "#111",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)",
  },

  verifiedDot: {
    position: "absolute",
    right: 2,
    bottom: 8,
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "#0a84ff",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    border: "3px solid white",
  },

  profileInfo: {
    flex: 1,
  },

  name: {
    margin: 0,
    fontSize: 34,
    fontWeight: 800,
    letterSpacing: -0.8,
  },

  email: {
    margin: "8px 0 0",
    fontSize: 18,
    color: "#777b85",
  },

  badgeRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 12,
  },

  badge: {
    background: "#050505",
    color: "#fff",
    borderRadius: 999,
    padding: "7px 12px",
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 1.2,
  },

  memberText: {
    color: "#8a8e98",
    fontSize: 14,
    fontWeight: 600,
  },

  editButton: {
    border: "none",
    background: "transparent",
    color: "#0a84ff",
    fontSize: 18,
    fontWeight: 800,
    cursor: "pointer",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 16,
    marginBottom: 24,
  },

  infoCard: {
    borderRadius: 28,
    background: "rgba(255,255,255,0.72)",
    backdropFilter: "blur(22px)",
    border: "1px solid rgba(255,255,255,0.8)",
    boxShadow: "0 20px 50px rgba(24,35,60,0.08)",
    padding: 24,
  },

  infoLabel: {
    margin: 0,
    fontSize: 13,
    color: "#777b85",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 1.4,
  },

  infoValue: {
    margin: "12px 0 4px",
    fontSize: 34,
    fontWeight: 900,
    letterSpacing: -1,
  },

  infoSubtext: {
    margin: 0,
    color: "#8a8e98",
    fontSize: 14,
  },

  card: {
    borderRadius: 34,
    background: "rgba(255,255,255,0.78)",
    backdropFilter: "blur(24px)",
    border: "1px solid rgba(255,255,255,0.82)",
    boxShadow: "0 26px 60px rgba(24,35,60,0.1)",
    padding: "28px 34px",
    marginBottom: 24,
  },

  sectionTitle: {
    margin: "0 0 18px",
    fontSize: 14,
    fontWeight: 900,
    color: "#70747e",
    textTransform: "uppercase",
    letterSpacing: 2.2,
  },

  detailRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 0",
    borderTop: "1px solid rgba(0,0,0,0.06)",
  },

  detailLabel: {
    fontSize: 18,
    fontWeight: 750,
  },

  detailValue: {
    fontSize: 18,
    color: "#777b85",
    textAlign: "right",
  },

  actionRow: {
    width: "100%",
    border: "none",
    background: "transparent",
    padding: "20px 0",
    borderTop: "1px solid rgba(0,0,0,0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    textAlign: "left",
    cursor: "pointer",
  },

  actionTitle: {
    margin: 0,
    fontSize: 19,
    fontWeight: 800,
    color: "#050505",
  },

  actionSubtitle: {
    margin: "6px 0 0",
    fontSize: 15,
    color: "#858995",
  },

  chevron: {
    fontSize: 34,
    lineHeight: 1,
    color: "#b0b4bd",
  },

  adminCard: {
    borderRadius: 34,
    background:
      "linear-gradient(135deg, rgba(5,5,5,0.96), rgba(40,40,45,0.94))",
    color: "#fff",
    boxShadow: "0 30px 70px rgba(0,0,0,0.22)",
    padding: 34,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 24,
  },

  adminLabel: {
    margin: 0,
    fontSize: 12,
    color: "#aaa",
    fontWeight: 900,
    letterSpacing: 2.2,
  },

  adminTitle: {
    margin: "8px 0",
    fontSize: 28,
    fontWeight: 850,
  },

  adminText: {
    margin: 0,
    color: "#bfc2ca",
    fontSize: 15,
  },

  adminButton: {
    border: "none",
    background: "#fff",
    color: "#000",
    borderRadius: 999,
    padding: "14px 22px",
    fontSize: 15,
    fontWeight: 850,
    cursor: "pointer",
  },

  footerText: {
    textAlign: "center",
    color: "#8a8e98",
    marginTop: 34,
    fontSize: 15,
  },

  loadingCard: {
    margin: "180px auto",
    width: 320,
    textAlign: "center",
    padding: 30,
    borderRadius: 28,
    background: "rgba(255,255,255,0.8)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
    fontWeight: 700,
  },

  "@media": {},
};